import { execute } from '@/graphql/execute';
import { MeQuery, ThemePreference, UserViewerSettings } from '@/graphql/graphql';
import { updateThemeMutation, updateViewerSettingsMutation } from '@/mutations/user.mutation';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

export interface iTokenUser {
  sessionId: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  sid: string;
  sub: string;
}

export const userAtom = atom<MeQuery['me'] | null>(null);

// Session storage for viewer settings when user is not set
const viewerSettingsStorageKey = '@actegon.gis.viewerSettings';
const storage = createJSONStorage<UserViewerSettings>(() => sessionStorage);

const getDefaultValue = (): UserViewerSettings => ({
  pointCloudPointSizeType: 'ADAPTIVE',
  pointCloudPointSize: 1.0,
  showLayerSource: false,
  showLayerUnderType: false,
  showImagePOIs: true,
  pointCloudPointBudget: 10_000_000,
  showPointCloudBoundingBoxes: false,
  googleStreetViewPoiColor: null,
});

const storageViewerSettingsAtom = atomWithStorage<UserViewerSettings>(
  viewerSettingsStorageKey,
  getDefaultValue(),
  storage,
);

export const userViewerSettingsAtom = atom(
  (get): UserViewerSettings => {
    const user = get(userAtom);
    if (user?.viewerSettings) {
      return { ...getDefaultValue(), ...user.viewerSettings, ...get(storageViewerSettingsAtom) };
    }
    return get(storageViewerSettingsAtom);
  },
  (get, set, newSettings: UserViewerSettings) => {
    const user = get(userAtom);

    set(storageViewerSettingsAtom, newSettings);

    if (user) {
      set(userAtom, {
        ...user,
        viewerSettings: newSettings,
      });

      execute(updateViewerSettingsMutation, {
        input: {
          pointCloudPointSizeType: newSettings.pointCloudPointSizeType,
          pointCloudPointSize: newSettings.pointCloudPointSize,
          showLayerSource: newSettings.showLayerSource,
          showLayerUnderType: newSettings.showLayerUnderType,
          showImagePOIs: newSettings.showImagePOIs,
          pointCloudPointBudget: newSettings.pointCloudPointBudget,
          showPointCloudBoundingBoxes: newSettings.showPointCloudBoundingBoxes,
          googleStreetViewPoiColor: newSettings.googleStreetViewPoiColor,
        },
      });
    }
  },
);

export type eTheme = ThemePreference;

export const userThemeAtom = atom(
  (get): eTheme | null => {
    const user = get(userAtom);
    if (!user) return null;
    return user.theme;
  },
  (get, set, theme: eTheme) => {
    const user = get(userAtom);
    if (user) {
      set(userAtom, { ...user, theme });
      execute(updateThemeMutation, { theme });
    }
  },
);
